import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SessionUserDto } from '@rhcst/shared';
import { api, ApiRequestError } from './api.js';

/**
 * Session state.
 *
 * The session lives on the server. This context holds only what the server told
 * us on the last `/auth/me` call, and never mirrors credentials or a token into
 * localStorage — there is nothing to mirror, because the cookie is HttpOnly.
 *
 * `status` distinguishes "we have not asked yet" from "asked, not signed in".
 * Without that distinction the app flashes the login screen on every reload.
 */

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface SessionContextValue {
  status: SessionStatus;
  user: SessionUserDto | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<SessionUserDto | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.get<{ user: SessionUserDto }>('/auth/me');
      setUser(result.user);
      setStatus('authenticated');
    } catch (error) {
      if (error instanceof ApiRequestError && error.isUnauthorized) {
        setUser(null);
        setStatus('anonymous');
        return;
      }
      // A network or server failure is not the same as being signed out, but
      // there is nothing useful to show either way, so treat it as anonymous
      // and let the login screen surface the real error on the next attempt.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.post<{ user: SessionUserDto }>('/auth/login', { email, password });
    setUser(result.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Clear local state even if the request failed: the operator asked to be
      // signed out, so the UI must not keep showing an authenticated shell.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return context;
}
